import * as React from 'react';
import {useRef, useState, useCallback} from 'react';
import type {ReactNode} from 'react';
import {useIsomorphicLayoutEffect, useForceUpdate} from '@sail/engine';
import * as Theme from '@sail/theme-classic';
import {earlyFraudWarning, warning} from '@sail/icons/react/Icon';
import {useResizeObserver} from '../../components/hooks/useResizeObserver';
import {view} from '../view';
import {css} from '../css';
import {CloseButton} from './CloseButton';
import {Icon} from './Icon';

export interface BannerProps {
  /**
   * @external
   */
  type?: 'default' | 'caution' | 'critical';
  /**
   * @external
   */
  description?: ReactNode;
  /**
   * @external
   */
  title?: ReactNode;
  /**
   * @external
   */
  onDismiss?: () => void;
  /**
   * @external
   */
  actions?: ReactNode;
}

const iconStyles = css({
  flexShrink: css.value(0),
  position: css.value('relative'),
  top: css.value('1px'),
});

const iconMap = {
  caution: <Icon icon={warning} uses={[iconStyles]} aria-hidden />,
  critical: <Icon icon={warning} uses={[iconStyles]} aria-hidden />,
  default: <Icon icon={earlyFraudWarning} uses={[iconStyles]} aria-hidden />,
};

const bannerStyles = css({
  display: css.value('flex'),
  alignItems: css.value('baseline'),
  flexBasis: css.value('100%'),
});

// add up the border, padding, and height of the title + description + actions
// container when there is only 1 line of text each and determine if the
// Banner is taller than that baseline height (meaning it has wrapped). This
// approach is pretty verbose because it needs to account for the default heights of items
// in the banner changing due to theming.
//
// Sidenote: We could just compare offsetTop of the title/description container and
// the actions container instead, but it's technically possible for them to have
// different offsetTops even when the actions are on the same line, if the content passed
// into actions is shorter than the height of the title/description container.
function checkIfWraps(
  titleEl: HTMLSpanElement | null,
  descriptionEl: HTMLSpanElement | null,
  containerEl: HTMLDivElement,
  actionsEl: HTMLDivElement | null,
) {
  const titleLineHeight = titleEl
    ? // eslint-disable-next-line @sail/ui-no-reflow
      parseInt(getComputedStyle(titleEl).lineHeight, 10)
    : 0;
  const descriptionLineHeight = descriptionEl
    ? // eslint-disable-next-line @sail/ui-no-reflow
      parseInt(getComputedStyle(descriptionEl).lineHeight, 10)
    : 0;

  const actionsHeight = actionsEl
    ? // eslint-disable-next-line @sail/ui-no-reflow
      parseInt(getComputedStyle(actionsEl).height, 10)
    : 0;
  // eslint-disable-next-line @sail/ui-no-reflow
  const {padding} = getComputedStyle(containerEl);
  // eslint-disable-next-line @sail/ui-no-reflow
  const {borderWidth} = getComputedStyle(containerEl);

  // if the button height is larger than the title + description, use that height
  // this can happen when there is a missing title or description and there is only one
  // line of text
  const innerContainerHeight =
    actionsHeight > titleLineHeight + descriptionLineHeight
      ? actionsHeight
      : titleLineHeight + descriptionLineHeight;
  return (
    // eslint-disable-next-line @sail/ui-no-reflow
    containerEl.offsetHeight >
    innerContainerHeight +
      parseInt(padding, 10) * 2 +
      parseInt(borderWidth, 10) * 2
  );
}

/**
 * @external
 */
export const Banner = view.span.create<BannerProps>(function Banner(
  {type = 'default', description, title, actions, onDismiss, ...props},
  View,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const descriptionRef = useRef<HTMLSpanElement | null>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [isWrapped, setIsWrapped] = useState(false);
  const forceUpdate = useForceUpdate();

  // The line requires a two-pass render because it's using a resize
  // observer on a parent node. When this component renders, that
  // ref is `null` is the resize observer doesn't work. The ref will
  // have a node during the second render, but note that (while
  // unlikely) if the ref changes node this component won't change
  // resize observers
  useIsomorphicLayoutEffect(() => {
    forceUpdate();
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, [containerRef]);

  const onResize = useCallback(() => {
    if (containerRef.current) {
      setIsWrapped(
        checkIfWraps(
          titleRef.current,
          descriptionRef.current,
          containerRef.current,
          actionsRef.current,
        ),
      );
    }
  }, [containerRef, titleRef, descriptionRef]);

  useIsomorphicLayoutEffect(onResize, [onResize]);

  useResizeObserver({
    node: containerRef.current,
    handler: onResize,
    // The layout effect above already handles the initial render
    avoidInitialEvent: true,
  });

  return (
    <View
      ref={containerRef}
      uses={[
        props,
        bannerStyles,
        Theme.BannerVariants.assign({type}),
        Theme.Banner,
      ]}
    >
      {iconMap[type]}
      <view.div
        css={{
          marginLeft: 150,
          display: css.value('flex'),
          flexWrap: css.value('wrap'),
          alignItems: css.value('center'),
          flexGrow: css.value(1),
        }}
      >
        <view.span
          css={{
            layout: 'column',
            marginRight: 150,
            flexWrap: css.value('wrap'),
          }}
          ref={titleRef}
        >
          {title ? (
            <view.span
              css={{fontWeight: 'semibold', display: css.value('block')}}
            >
              {title}
            </view.span>
          ) : null}
          {description ? (
            <view.span ref={descriptionRef}>{description}</view.span>
          ) : null}
        </view.span>
        {actions ? (
          <view.div
            css={{
              layout: 'row',
              gap: 'small',
              width: css.value('auto'),
              flexWrap: css.value('wrap'),
              flexShrink: css.value(0),
              marginTop: isWrapped ? 'small' : 0,
              marginLeft: isWrapped ? 0 : css.value('auto'),
              marginRight: 150,
            }}
            ref={actionsRef}
          >
            {actions}
          </view.div>
        ) : null}
      </view.div>
      {onDismiss ? (
        <CloseButton
          css={{
            alignSelf: isWrapped ? css.value('start') : css.value('center'),
          }}
          onDismiss={onDismiss}
        />
      ) : null}
    </View>
  );
});
